import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlayCircle, Search, X } from 'lucide-react';
import { usePmProperty } from '@/hooks/usePmProperty';
import {
  getDsarQueue,
  getRetentionStatus,
  triggerRetentionSweep,
  updateDsarStatus,
} from '@/services/api.client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ConfirmDialog } from '@/components/pm/ConfirmDialog';
import type { DsarQueueItem, PmDsarTransition } from '@/types';

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  submitted: 'secondary',
  processing: 'default',
  fulfilled: 'outline',
  failed: 'destructive',
};

type StatusFilter = 'all' | 'submitted' | 'processing' | 'fulfilled' | 'failed';

const FILTER_LABELS: Record<StatusFilter, string> = {
  all: 'All',
  submitted: 'Submitted',
  processing: 'Processing',
  fulfilled: 'Fulfilled',
  failed: 'Failed',
};

const RETENTION_CATEGORIES = [
  'Visitor access logs',
  'Expired invitations',
  'Recurring passes (after expiry)',
  'Domestic worker passes (after expiry)',
  'Delivery passes (after use or expiry)',
  'Taxi passes (after expiry)',
  'Walk-in requests',
];

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string | undefined;
}) {
  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4">
      <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--text-primary)]">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-[var(--text-tertiary)]">{hint}</p>}
    </div>
  );
}

export function CompliancePage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const qc = useQueryClient();

  const [detailTarget, setDetailTarget] = useState<DsarQueueItem | null>(null);
  const [pendingTransition, setPendingTransition] = useState<PmDsarTransition | null>(null);
  const [confirmSweepOpen, setConfirmSweepOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');

  const dsarQuery = useQuery({
    queryKey: ['pm', 'dsar', propertyId],
    queryFn: () => getDsarQueue(propertyId),
    enabled: !!propertyId,
  });

  const retentionQuery = useQuery({
    queryKey: ['pm', 'retention', propertyId],
    queryFn: () => getRetentionStatus(propertyId),
    enabled: !!propertyId,
  });

  const updateMutation = useMutation({
    mutationFn: ({ status, reason }: { status: PmDsarTransition; reason?: string }) =>
      updateDsarStatus(propertyId, detailTarget?.id ?? '', { status, ...(reason && { reason }) }),
    onSuccess: (_, variables) => {
      void qc.invalidateQueries({ queryKey: ['pm', 'dsar', propertyId] });
      setDetailTarget(null);
      setPendingTransition(null);
      const labels: Record<PmDsarTransition, string> = {
        processing: 'Marked in-progress',
        fulfilled: 'Marked fulfilled',
        failed: 'Marked failed',
      };
      toast.success(labels[variables.status]);
    },
    onError: () => {
      toast.error('Could not update DSAR status. Please try again.');
    },
  });

  const sweepMutation = useMutation({
    mutationFn: () => triggerRetentionSweep(propertyId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'retention', propertyId] });
      setConfirmSweepOpen(false);
      toast.success('Retention sweep started — results will appear shortly.');
    },
    onError: () => {
      setConfirmSweepOpen(false);
      toast.error('Could not start the retention sweep. Please try again.');
    },
  });

  const allDsars = useMemo(() => dsarQuery.data ?? [], [dsarQuery.data]);

  const filteredDsars = useMemo(() => {
    const term = searchInput.trim().toLowerCase();
    return allDsars.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (term && !d.referenceNumber.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [allDsars, statusFilter, searchInput]);

  const stats = useMemo(() => {
    const open = allDsars.filter(
      (d) => d.status === 'submitted' || d.status === 'processing',
    ).length;
    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);
    const fulfilledThisMonth = allDsars.filter(
      (d) =>
        d.status === 'fulfilled' &&
        d.fulfilledAt !== null &&
        new Date(d.fulfilledAt).getTime() >= monthStart.getTime(),
    ).length;
    return { open, fulfilledThisMonth };
  }, [allDsars]);

  const isTerminal = detailTarget?.status === 'fulfilled' || detailTarget?.status === 'failed';

  const lastSweepAt = retentionQuery.data?.lastSweepAt;
  const lastSweepHint = lastSweepAt ? new Date(lastSweepAt).toLocaleDateString() : 'No sweeps yet';

  return (
    <div className="space-y-6">
      <h1 className="text-2xl">Compliance</h1>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Open DSARs" value={stats.open} hint="Submitted or processing" />
        <StatCard
          label="Fulfilled this month"
          value={stats.fulfilledThisMonth}
          hint="Closed requests"
        />
        <StatCard
          label="Last sweep"
          value={retentionQuery.isLoading ? '…' : lastSweepAt ? lastSweepHint : '—'}
          hint={
            retentionQuery.data?.lastSweepStatus
              ? `Status: ${retentionQuery.data.lastSweepStatus}`
              : undefined
          }
        />
        <StatCard
          label="Records anonymised"
          value={retentionQuery.data?.recordsAnonymised ?? 0}
          hint="In the last sweep"
        />
      </div>

      <Tabs defaultValue="dsar">
        <TabsList>
          <TabsTrigger value="dsar">DSAR Queue</TabsTrigger>
          <TabsTrigger value="retention">Data Retention</TabsTrigger>
        </TabsList>

        <TabsContent value="dsar" className="mt-4 space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1.5">
              {(Object.keys(FILTER_LABELS) as StatusFilter[]).map((f) => (
                <Button
                  key={f}
                  type="button"
                  size="sm"
                  variant={statusFilter === f ? 'default' : 'outline'}
                  onClick={() => setStatusFilter(f)}
                >
                  {FILTER_LABELS[f]}
                </Button>
              ))}
            </div>
            <div className="relative sm:w-72">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {dsarQuery.isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load DSAR queue</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : dsarQuery.isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 rounded-lg" />
              ))}
            </div>
          ) : allDsars.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--text-tertiary)] rounded-md border">
              No data subject requests yet.
            </div>
          ) : filteredDsars.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--text-tertiary)] rounded-md border">
              No requests match the current filter.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Requested by</TableHead>
                  <TableHead>Fulfilled</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDsars.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer"
                    onClick={() => setDetailTarget(item)}
                  >
                    <TableCell className="font-mono text-sm">{item.referenceNumber}</TableCell>
                    <TableCell className="capitalize">{item.requestType}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[item.status] ?? 'default'}>
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(item.submittedAt).toLocaleDateString()}</TableCell>
                    <TableCell>{item.requestedBy.name ?? item.requestedBy.email ?? '—'}</TableCell>
                    <TableCell>
                      {item.fulfilledAt ? new Date(item.fulfilledAt).toLocaleDateString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabsContent>

        <TabsContent value="retention" className="mt-4 space-y-4">
          {/* Status panel */}
          <div className="rounded-lg border p-6 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg">Status</h2>
                <p className="text-sm text-[var(--text-tertiary)] mt-1">
                  Sweeps run automatically every night at 02:00 EAT. You can also trigger one
                  manually.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setConfirmSweepOpen(true)}
                disabled={sweepMutation.isPending}
                className="gap-1.5 shrink-0"
              >
                <PlayCircle className="h-4 w-4" />
                {sweepMutation.isPending ? 'Starting…' : 'Trigger sweep now'}
              </Button>
            </div>
            {retentionQuery.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 rounded-lg" />
                ))}
              </div>
            ) : retentionQuery.isError ? (
              <div
                role="alert"
                className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
              >
                <p className="text-sm font-medium">Couldn&apos;t load retention status</p>
                <p className="text-sm text-muted-foreground">Please try again later.</p>
              </div>
            ) : (
              <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="rounded-md bg-muted/50 p-3">
                  <dt className="text-xs font-medium text-muted-foreground uppercase">
                    Last sweep
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {retentionQuery.data?.lastSweepAt
                      ? new Date(retentionQuery.data.lastSweepAt).toLocaleString()
                      : '—'}
                  </dd>
                </div>
                <div className="rounded-md bg-muted/50 p-3">
                  <dt className="text-xs font-medium text-muted-foreground uppercase">
                    Records anonymised
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {retentionQuery.data?.recordsAnonymised ?? 0}
                  </dd>
                </div>
                <div className="rounded-md bg-muted/50 p-3">
                  <dt className="text-xs font-medium text-muted-foreground uppercase">
                    Next scheduled run
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {retentionQuery.data?.nextRunAt
                      ? new Date(retentionQuery.data.nextRunAt).toLocaleString()
                      : '—'}
                  </dd>
                </div>
              </dl>
            )}
          </div>

          {/* Retention rules */}
          <div className="rounded-lg border p-6">
            <h2 className="text-lg">Retention rules in effect</h2>
            <p className="text-sm text-[var(--text-tertiary)] mt-1">
              After{' '}
              <span className="font-medium text-[var(--text-primary)]">
                {retentionQuery.data?.retentionDays ?? '—'} days
              </span>{' '}
              the following data is anonymised. Names are replaced with{' '}
              <code className="text-xs">[anonymised]</code>; phone numbers, vehicle plates, and ID
              numbers are cleared.
            </p>
            <ul className="mt-3 space-y-1.5">
              {RETENTION_CATEGORIES.map((c) => (
                <li key={c} className="text-sm flex items-center gap-2">
                  <span className="h-1 w-1 rounded-full bg-[var(--text-tertiary)]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>

          {/* Recent sweep history */}
          <div className="rounded-lg border p-6">
            <h2 className="text-lg">Recent sweeps</h2>
            <p className="text-sm text-[var(--text-tertiary)] mt-1 mb-3">
              The last five runs against this property.
            </p>
            {retentionQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 rounded" />
                ))}
              </div>
            ) : (retentionQuery.data?.recentSweeps?.length ?? 0) === 0 ? (
              <p className="text-sm text-[var(--text-tertiary)]">
                No sweeps have run on this property yet.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Run at</TableHead>
                    <TableHead className="text-right">Anonymised</TableHead>
                    <TableHead className="text-right">Processed</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {retentionQuery.data?.recentSweeps.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-sm">
                        {new Date(s.runAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {s.recordsAnonymised}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {s.recordsProcessed}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            s.status === 'success' || s.status === 'completed'
                              ? 'outline'
                              : 'destructive'
                          }
                        >
                          {s.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* DSAR detail side-panel */}
      <Sheet
        open={!!detailTarget}
        onOpenChange={(open) => {
          if (!open) setDetailTarget(null);
        }}
      >
        <SheetContent className="w-96 sm:w-96 flex flex-col gap-4 overflow-auto">
          <SheetHeader>
            <SheetTitle className="font-mono text-sm">{detailTarget?.referenceNumber}</SheetTitle>
            <SheetDescription className="capitalize">
              {detailTarget?.requestType} request
            </SheetDescription>
          </SheetHeader>

          {detailTarget && (
            <div className="space-y-4 px-1">
              <div className="flex items-center gap-2">
                <Badge variant={STATUS_VARIANTS[detailTarget.status] ?? 'default'}>
                  {detailTarget.status}
                </Badge>
              </div>

              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    Requested by
                  </dt>
                  <dd className="mt-0.5">
                    {detailTarget.requestedBy.name ?? detailTarget.requestedBy.email ?? '—'}
                  </dd>
                  {detailTarget.requestedBy.email && detailTarget.requestedBy.name && (
                    <dd className="text-xs text-muted-foreground">
                      {detailTarget.requestedBy.email}
                    </dd>
                  )}
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                    Submitted
                  </dt>
                  <dd className="mt-0.5">{formatDate(detailTarget.submittedAt)}</dd>
                </div>
                {detailTarget.processingStartedAt && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Started processing
                    </dt>
                    <dd className="mt-0.5">{formatDate(detailTarget.processingStartedAt)}</dd>
                  </div>
                )}
                {detailTarget.fulfilledAt && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Fulfilled
                    </dt>
                    <dd className="mt-0.5">{formatDate(detailTarget.fulfilledAt)}</dd>
                  </div>
                )}
                {detailTarget.additionalDetails && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Notes from requester
                    </dt>
                    <dd className="mt-0.5 whitespace-pre-wrap">{detailTarget.additionalDetails}</dd>
                  </div>
                )}
                {detailTarget.failureReason && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                      Failure reason
                    </dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-destructive">
                      {detailTarget.failureReason}
                    </dd>
                  </div>
                )}
              </dl>

              {!isTerminal && (
                <div className="flex flex-col gap-2 pt-2 border-t">
                  {detailTarget.status === 'submitted' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateMutation.mutate({ status: 'processing' })}
                      disabled={updateMutation.isPending}
                    >
                      Mark in-progress
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => updateMutation.mutate({ status: 'fulfilled' })}
                    disabled={updateMutation.isPending}
                  >
                    Mark fulfilled
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setPendingTransition('failed')}
                    disabled={updateMutation.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Reject (record reason)
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={pendingTransition === 'failed'}
        title="Reject this request"
        description="Provide a reason. This will be recorded against the request and visible in the requester's DSAR history."
        confirmLabel="Reject"
        requireReason
        reasonLabel="Reason"
        minReasonLength={10}
        loading={updateMutation.isPending}
        onConfirm={(reason) => updateMutation.mutate({ status: 'failed', reason: reason ?? '' })}
        onCancel={() => setPendingTransition(null)}
      />

      <ConfirmDialog
        open={confirmSweepOpen}
        title="Trigger retention sweep"
        description="This will run the retention policies for this property now. The sweep happens in the background and may take a few minutes."
        confirmLabel="Start sweep"
        loading={sweepMutation.isPending}
        onConfirm={() => sweepMutation.mutate()}
        onCancel={() => setConfirmSweepOpen(false)}
      />
    </div>
  );
}
